import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Icon from '../components/Icon';
import CTASection from '../components/CTASection';
import { ABOUT_VALUES, ABOUT_STATS, ABOUT_TEAM, ABOUT_MIGRATION_PAINS, ABOUT_CONTROL } from '../data/content';
import { IMAGES } from '../data/images';

export default function About() {
  const { t } = useLanguage();
  return (
    <div>
      <PageHero eyebrow={t('nav.about')} title="Take control of your utility bill lifecycle" subtitle="We help organisations own the end-to-end utility bill lifecycle — from collection to payments and reporting. Own your software, own your data, and bring transparency and efficiency back in-house." />

      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow="Who we are" title="Built by people who have run UBM at scale" />
              <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
                <p>BillPortal was founded by a team with <strong className="text-slate-900 dark:text-white">over 50 years of collective experience</strong> in Utility Bill Management — across SaaS, managed services and hybrid operating models, delivered globally.</p>
                <p>Across that journey we heard the same challenge again and again: organisations want greater <strong className="text-slate-900 dark:text-white">transparency, flexibility and control</strong> over their utility operations — without having to start over every time a provider changes.</p>
                <p>So we built BillPortal to provide the technology, automation and AI that help organisations run utility bill operations more efficiently — while we take care of continuously evolving the platform behind them.</p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10">
                <img src={IMAGES.team} alt="BillPortal team" className="h-full max-h-96 w-full object-cover" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <blockquote className="mx-auto mt-14 max-w-4xl rounded-2xl border-l-4 border-brand bg-brand/5 p-8 dark:bg-brand/10" data-testid="about-belief">
              <p className="font-display text-xl font-bold leading-snug text-slate-900 dark:text-white sm:text-2xl">"Your data, processes and operational knowledge should stay with you — regardless of who manages your utility bills."</p>
              <footer className="mt-3 text-sm font-medium text-brand">The belief BillPortal was built on</footer>
            </blockquote>
          </Reveal>

          <Reveal delay={160}>
            <div className="mx-auto mt-8 max-w-4xl space-y-4 text-base leading-relaxed text-slate-600 dark:text-slate-300">
              <p>Think of it the way organisations already rely on platforms like <strong className="text-slate-900 dark:text-white">SAP, Salesforce, Yardi or RealPage</strong>: you keep the technology and your data in-house, while your teams run day-to-day operations — whether that's an internal team or an offshore partner. BillPortal brings that same model to utility bill management.</p>
              <p>And BillPortal never stands still. We actively learn from our customers and users, turning real operational feedback and industry best practices into better workflows, automation and capabilities for everyone on the platform.</p>
              <p className="text-lg font-semibold text-slate-900 dark:text-white">We manage the technology, so you can focus on running better utility operations.</p>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24" data-testid="problem-section">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="The problem" title="Switching providers shouldn't mean starting from scratch" subtitle="When you change an incumbent, you inherit their software — not a choice. Everything has to be rebuilt, and the bill lands on you." />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ABOUT_MIGRATION_PAINS.map((p, i) => (
              <Reveal key={p.title} delay={i * 60}>
                <div className="flex h-full gap-4 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={p.icon} className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{p.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-brand/30 bg-brand/5 p-6 text-center">
              <p className="text-base font-medium text-slate-900 dark:text-white">The result: huge cost, wasted effort, and disruption every couple of years — made worse when key people move on mid-transition.</p>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="control-section">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="Our answer" title="BillPortal puts you back in control" subtitle="Own your data and take control — instead of re-migrating every couple of years. Change your incumbent or manage it in-house, while your data stays exactly where it belongs: with you." />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ABOUT_CONTROL.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-brand/40 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={c.icon} className="h-6 w-6" /></span>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="What we value" title="Principles that guide our product" /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT_VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={v.icon} className="h-6 w-6" /></span>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="leadership-section">
        <Container>
          <Reveal><SectionHeading eyebrow="Our team" title="Meet the people behind BillPortal" subtitle="A founding team with decades of combined experience across energy, technology and commercial growth." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {ABOUT_TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 80}>
                <div className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 text-center transition-colors hover:border-brand/40 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`team-card-${i}`}>
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="mx-auto h-40 w-40 rounded-full object-cover object-top ring-4 ring-brand/10" />
                  ) : (
                    <span className="mx-auto flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand/70 font-display text-4xl font-bold text-white ring-4 ring-brand/10">{m.initials}</span>
                  )}
                  <h3 className="mt-6 font-display text-xl font-bold text-slate-900 dark:text-white">{m.name}</h3>
                  <p className="mt-1 text-sm font-medium text-brand">{m.role}</p>
                  {m.location && (
                    <p className="mt-1.5 inline-flex items-center justify-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                      <Icon name="MapPin" className="h-3.5 w-3.5" /> {m.location}
                    </p>
                  )}
                  <p className="mt-5 flex-1 text-left text-sm leading-relaxed text-slate-600 dark:text-slate-300">{m.bio}</p>
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mx-auto mt-6 inline-flex w-fit items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-brand dark:text-slate-400"
                      data-testid={`team-linkedin-${i}`}
                    >
                      <Icon name="Linkedin" className="h-4 w-4" /> Connect on LinkedIn
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="px-5 py-14 sm:px-8">
        <Container>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {ABOUT_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 60}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="font-display text-2xl font-bold text-brand sm:text-3xl">{s.value}</div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTASection />
    </div>
  );
}
